import React, { useEffect, Fragment, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { Card, CardBody, Row, Col, Container, FormGroup, Button, UncontrolledTooltip } from "reactstrap";
import { getInvoiceById } from "./store/salesInvoiceItemSlice";
import { exportInvoiceDetailReport, handleXeroUpload } from "./store/salesInvoiceReportSlice";
import CommonDataTable from "../../../share-components/table/commonDataTable";
import Breadcrumb from "../../../layout/breadcrumb";
import withReducer from "../../../store/withReducer";
import reportReducer from "../index";
import { toast } from "react-toastify";



const SalesInvoiceView = () => {
    const { id } = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const [typeFilter, setTypeFilter] = useState("All");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [timer, setTimer] = useState(null);
    const [isXeroUploading, setIsXeroUploading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const { selectedInvoice, invoiceItems, totalItems, viewLoading } = useSelector(
        ({ report }) => report.salesInvoiceItemReport
    );

    const formatNumber = (value) => {
        const num = parseFloat(value || 0);
        return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatNumberOnly = (value) => {
        const num = Number(value || 0);

        return num.toLocaleString("en-US", {
            minimumFractionDigits: num % 1 === 0 ? 0 : 2,
            maximumFractionDigits: 2,
        });
    };

    useEffect(() => {
        dispatch(getInvoiceById({ invoiceId: id, page, pageSize, search, type: typeFilter }));
    }, [id, page, pageSize, search, typeFilter, dispatch]);

    const handleTypeChange = (value) => {
        setTypeFilter(value);
        setPage(1);
    };

    const handleExport = () => {
        if (isExporting) return;
        setIsExporting(true);
        dispatch(exportInvoiceDetailReport(id)).then((res) => {
            const link = document.createElement("a");
            link.href = res.payload.response.data;
            link.setAttribute("download", "");
            document.body.appendChild(link);
            link.click();
            link.remove();
        }).finally(() => setIsExporting(false));
    };

    const handleSyncToXero = () => {
        if (isXeroUploading) return;
        setIsXeroUploading(true);
        dispatch(handleXeroUpload(id)).then((res) => {
            if (res.payload.response.data.code === '200') {
                toast.success("Invoice uploaded to Xero successfully");
            } else {
                toast.error("Failed to upload invoice to Xero");
            }
        }).catch(() => {
            toast.error("Error uploading invoice");
        }).finally(() => setIsXeroUploading(false));
    };

    const columns = [
        {
            name: <span title="Order Number">Order Number</span>,
            selector: row => row.order?.order_name,
            cell: row => row.order?.order_name,
            left: true,
            sortable: true,
            grow: 1
        },
        {
            name: <span title="Type">Type</span>,
            selector: row => row.type,
            cell: row => <span title={row.type}>{row.type}</span>,
            left: true,
            sortable: true,
        },
        {
            name: <span title="Processed Date">Processed Date</span>,
            selector: row => row.processed_date,
            sortable: true,
            left: true
        },
        {
            name: <span title="Stock Keeping Unit">SKU</span>,
            selector: row => row.product_variant?.sku,
            cell: row => <span title={row.product_variant?.sku}>{row.product_variant?.sku}</span>,
            left: true,
            sortable: true,
            grow: 1
        },
        {
            name: <span title="Quantity">Quantity</span>,
            selector: row => formatNumberOnly(row.quantity),
            sortable: true,
            right: true
        },
        {
            name: <span title="Unit Cost">Unit Cost</span>,
            selector: row => formatNumberOnly(row.unit_cost),
            sortable: true,
            right: true
        },
        {
            name: <span title="Cost">Cost (£)</span>,
            selector: row => formatNumber(row.cost),
            sortable: true,
            right: true
        },
        {
            name: <span title="Tax">Tax (£)</span>,
            selector: row => formatNumber(row.tax),
            sortable: true,
            right: true
        },
        {
            name: <span title="Net Cost">Net Cost (£)</span>,
            selector: row => formatNumber(row.cost_with_tax),
            sortable: true,
            right: true
        },
    ];

    const dataMenu = [
        {
            type: 1,
            text: 'Sales Invoices - Goddiva',
            link: '/report/goddiva-invoices'
        },
        {
            type: 0,
            text: 'View Sales Invoice Details',
            link: ''
        },
    ];

    useEffect(() => {
        const newTimer = setTimeout(() => {
            setSearch(searchInput);
        }, 500);
        setTimer(newTimer);
        return () => clearTimeout(newTimer);
    }, [searchInput]);

    return (
        <Fragment>
            {!selectedInvoice && viewLoading && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    background: 'rgba(255,255,255,0.7)', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <i className="fa fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#7366ff' }}></i>
                </div>
            )}
            <Breadcrumb title="Sales Invoices Items - Goddiva" data={dataMenu} />
            <Container fluid={true} className="product-wrapper">
                <div className="product-grid">
                    <div className="feature-products">
                        {selectedInvoice && (
                            <Card className="mb-4">
                                <CardBody>
                                    <Row className="mb-3 align-items-center">
                                        <Col className="text-right">
                                            <Button
                                                id="btn-back"
                                                size="sm"
                                                color="light"
                                                className="mr-2"
                                                onClick={() => history.push('/report/goddiva-invoices')}
                                            >
                                                <i className="fa fa-arrow-left mr-1"></i> Back
                                            </Button>
                                            <UncontrolledTooltip placement="top" target="btn-back">
                                                Back to Invoices
                                            </UncontrolledTooltip>
                                            <Button
                                                id="btn-sync-xero"
                                                size="sm"
                                                color="warning"
                                                className="mr-2"
                                                disabled={isXeroUploading}
                                                onClick={handleSyncToXero}
                                            >
                                                {isXeroUploading
                                                    ? <i className="fa fa-spinner fa-spin mr-1"></i>
                                                    : <i className="fa fa-refresh mr-1"></i>}
                                                Sync to Xero
                                            </Button>
                                            <UncontrolledTooltip placement="top" target="btn-sync-xero">
                                                Upload Invoice to Xero
                                            </UncontrolledTooltip>
                                            <Button
                                                id="btn-export"
                                                size="sm"
                                                color="primary"
                                                disabled={isExporting}
                                                onClick={handleExport}
                                            >
                                                {isExporting
                                                    ? <i className="fa fa-spinner fa-spin mr-1"></i>
                                                    : <i className="fa fa-download mr-1"></i>}
                                                Export
                                            </Button>
                                            <UncontrolledTooltip placement="top" target="btn-export">
                                                Export Invoice Details
                                            </UncontrolledTooltip>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md="4" xl="4" sm="12"><b>Invoice Date:</b> {selectedInvoice.invoice_date}</Col>
                                        <Col md="4" xl="4" sm="12"><b>Order Dates:</b> {selectedInvoice.order_start_date} - {selectedInvoice.order_end_date}</Col>
                                        <Col md="4" xl="4" sm="12"><b>Return Dates:</b> {selectedInvoice.return_start_date} - {selectedInvoice.return_end_date}</Col>
                                    </Row>
                                    <Row className="mt-3">
                                        <Col md="4" xl="4" sm="12"><b>Ordered Qty:</b> {formatNumberOnly(selectedInvoice.total_ordered_qty)}</Col>
                                        <Col md="4" xl="4" sm="12"><b>Returned Qty:</b> {formatNumberOnly(selectedInvoice.total_returned_qty)}</Col>
                                        <Col md="4" xl="4" sm="12"><b>Net Qty:</b> {formatNumberOnly(selectedInvoice.total_net_qty)}</Col>
                                    </Row>
                                    <Row className="mt-3">
                                        <Col md="4" xl="4" sm="12"><b>Total Cost (£):</b> {formatNumber(selectedInvoice.total_cost)}</Col>
                                        <Col md="4" xl="4" sm="12"><b>Tax (£):</b> {formatNumber(selectedInvoice.total_tax)}</Col>
                                        <Col md="4" xl="4" sm="12"><b>Net Amount (£):</b> {formatNumber(selectedInvoice.total_cost_with_tax)}</Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        )}
                        <Row className="mb-3">
                            <Col xl="6" sm="12">
                                <FormGroup className="mb-0">
                                    <input
                                        type="text"
                                        placeholder="Search by SKU ..."
                                        className="form-control"
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col xl="6" sm="12">
                                <FormGroup className="mb-0">
                                    <select
                                        className="form-control"
                                        value={typeFilter}
                                        onChange={(e) => handleTypeChange(e.target.value)}
                                    >
                                        <option value="All">All</option>
                                        <option value="Order">Order</option>
                                        <option value="Return">Return</option>
                                    </select>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Card>
                            <CardBody>
                                <div className="table-responsive product-table">
                                    <CommonDataTable
                                        headerColumns={columns}
                                        gridData={invoiceItems}
                                        noAction
                                        fixedHeader
                                        fixedHeaderScrollHeight="600px"
                                        progressPending={viewLoading}
                                        progressComponent={
                                            <div className="p-4">
                                                <i className="fa fa-spinner fa-spin mr-2"></i> Loading...
                                            </div>
                                        }
                                        paginationServer
                                        paginationTotalRows={totalItems}
                                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                        onChangePage={(p) => setPage(p)}
                                        onChangeRowsPerPage={(size) => { setPageSize(size); setPage(1); }}
                                    />
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </Container>
        </Fragment>
    );
};

export default withReducer([{ report: reportReducer }])(SalesInvoiceView);
